import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { InsertChildRiskOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertChildRiskOversightResultInput = {
  OversightAssessmentId: string;
  RiskIds: string[];
  ControlType: RiskAssessmentResultControlType;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertChildRiskOversightResultMutation => ({
  insertChildRiskOversightResult: {
    __typename: 'InsertChildRiskAssessmentResultOutput',
    Ids: trpcData.Ids,
  },
});

export const useInsertChildRiskOversightResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.insertChildRiskOversightResult.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          // Apollo parity: evictField(cache, 'risk_controlled_oversight_result')
          // + 'risk_uncontrolled_oversight_result'
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.oversightResultById.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.riskOversightResultById.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentRiskAssessmentResultsByRiskId.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.latestOversightAssessmentRiskAssessmentResultsByRiskId.pathKey(),
          }),
          // Apollo parity: evictField(cache, '*_oversight_result_aggregate')
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
          }),
          // Apollo parity: evictField(cache, 'oversight_assessment')
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightActivityRegister.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightActivityCount.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertChildRiskOversightResult: async (
      variables: InsertChildRiskOversightResultInput
    ): Promise<InsertChildRiskOversightResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
